/**
 * ComparisonView Component
 *
 * Side-by-side comparison of 2-3 scenarios
 * with metrics charts and diff highlighting.
 */

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
} from 'recharts';
import type { ScenarioWithResult } from '../../contexts/ScenarioContext';

interface ComparisonViewProps {
  scenarios: ScenarioWithResult[];
  onRemove?: (id: string) => void;
}

// Icons
const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const scenarioColors = ['#3b82f6', '#22c55e', '#f59e0b'];

export function ComparisonView({ scenarios, onRemove }: ComparisonViewProps) {
  if (scenarios.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-text-tertiary dark:text-text-secondary">
        <div className="text-center">
          <p className="text-lg font-medium mb-2">No scenarios selected</p>
          <p className="text-sm">Select 2-3 scenarios to compare</p>
        </div>
      </div>
    );
  }

  if (scenarios.length === 1) {
    return (
      <div className="flex items-center justify-center h-64 text-text-tertiary dark:text-text-secondary">
        <div className="text-center">
          <p className="text-lg font-medium mb-2">One scenario selected</p>
          <p className="text-sm">Select at least one more scenario to compare</p>
        </div>
      </div>
    );
  }

  // Extract common metrics for comparison
  const allMetrics = new Set<string>();
  scenarios.forEach(s => {
    if (s.latestResult?.metrics) {
      Object.keys(s.latestResult.metrics).forEach(k => allMetrics.add(k));
    }
  });

  const metricsArray = Array.from(allMetrics);

  // Build chart data
  const chartData = metricsArray.map(metric => {
    const dataPoint: Record<string, string | number> = { name: formatMetricName(metric) };
    scenarios.forEach((s, i) => {
      const value = s.latestResult?.metrics?.[metric];
      dataPoint[`scenario${i}`] = typeof value === 'number' ? value : 0;
      dataPoint[`name${i}`] = s.name;
    });
    return dataPoint;
  });

  return (
    <div className="space-y-6">
      {/* Scenario headers */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {scenarios.map((scenario, index) => (
          <div
            key={scenario.id}
            className="p-4 rounded-lg border-2"
            style={{ borderColor: scenarioColors[index] }}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">
                  {scenario.name}
                </h3>
                <p className="text-sm text-text-tertiary dark:text-text-secondary">
                  {scenario.scenarioType} • v{scenario.version}
                </p>
              </div>
              {onRemove && (
                <button
                  onClick={() => onRemove(scenario.id)}
                  className="text-text-secondary hover:text-surface-400 dark:hover:text-text-secondary"
                >
                  <CloseIcon />
                </button>
              )}
            </div>
            <div
              className="mt-2 w-full h-1 rounded"
              style={{ backgroundColor: scenarioColors[index] }}
            />
          </div>
        ))}
      </div>

      {/* Metrics comparison chart */}
      {chartData.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Metrics Comparison
          </h4>
          <div style={{ width: '100%', height: 400 }}>
            <ResponsiveContainer>
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
              >
                <XAxis type="number" />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 12 }}
                />
                <Tooltip content={<CustomTooltip scenarios={scenarios} />} />
                <Legend />
                {scenarios.map((scenario, index) => (
                  <Bar
                    key={scenario.id}
                    dataKey={`scenario${index}`}
                    name={scenario.name}
                    fill={scenarioColors[index]}
                    radius={[0, 4, 4, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Detailed metrics table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-surface-400 dark:text-text-secondary">
                Metric
              </th>
              {scenarios.map((s, i) => (
                <th
                  key={s.id}
                  className="px-4 py-3 text-right text-sm font-medium"
                  style={{ color: scenarioColors[i] }}
                >
                  {s.name}
                </th>
              ))}
              <th className="px-4 py-3 text-right text-sm font-medium text-surface-400 dark:text-text-secondary">
                Diff
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {metricsArray.map(metric => {
              const values = scenarios.map(s => {
                const val = s.latestResult?.metrics?.[metric];
                return typeof val === 'number' ? val : null;
              });

              const validValues = values.filter((v): v is number => v !== null);
              const min = validValues.length > 0 ? Math.min(...validValues) : 0;
              const max = validValues.length > 0 ? Math.max(...validValues) : 0;
              const diff = max - min;
              const diffPercent = min !== 0 ? ((diff / min) * 100).toFixed(1) : '0';

              return (
                <tr key={metric} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 text-sm text-text-secondary dark:text-text-secondary">
                    {formatMetricName(metric)}
                  </td>
                  {values.map((value, i) => {
                    const isMax = value === max && validValues.length > 1;
                    const isMin = value === min && validValues.length > 1;
                    return (
                      <td
                        key={i}
                        className={`px-4 py-3 text-sm text-right font-medium ${
                          isMax ? 'text-green-600 dark:text-green-400' :
                          isMin ? 'text-red-600 dark:text-red-400' :
                          'text-gray-900 dark:text-white'
                        }`}
                      >
                        {value !== null ? formatValue(value, metric) : '-'}
                        {isMax && ' ↑'}
                        {isMin && ' ↓'}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-sm text-right text-text-tertiary dark:text-text-secondary">
                    {diff !== 0 ? `${diffPercent}%` : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Configuration differences */}
      <ConfigDiff scenarios={scenarios} />
    </div>
  );
}

function CustomTooltip({
  active,
  payload,
  label,
  scenarios,
}: {
  active?: boolean;
  payload?: Array<{ value: number; dataKey: string; fill: string }>;
  label?: string;
  scenarios: ScenarioWithResult[];
}) {
  if (!active || !payload) return null;

  return (
    <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-3 border border-gray-200 dark:border-gray-700">
      <p className="font-medium text-gray-900 dark:text-white mb-2">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 text-sm">
          <div
            className="w-3 h-3 rounded"
            style={{ backgroundColor: entry.fill }}
          />
          <span className="text-surface-400 dark:text-text-secondary">
            {scenarios[i]?.name}:
          </span>
          <span className="font-medium text-gray-900 dark:text-white">
            {entry.value.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}

function ConfigDiff({ scenarios }: { scenarios: ScenarioWithResult[] }) {
  // Find configuration differences
  const allKeys = new Set<string>();
  scenarios.forEach(s => {
    if (s.config) {
      Object.keys(s.config).forEach(k => allKeys.add(k));
    }
  });

  const differences: Array<{
    key: string;
    values: Array<{ scenario: string; value: unknown }>;
    isDifferent: boolean;
  }> = [];

  allKeys.forEach(key => {
    const values = scenarios.map(s => ({
      scenario: s.name,
      value: s.config?.[key],
    }));

    const uniqueValues = new Set(values.map(v => JSON.stringify(v.value)));
    const isDifferent = uniqueValues.size > 1;

    differences.push({ key, values, isDifferent });
  });

  const significantDiffs = differences.filter(d => d.isDifferent);

  if (significantDiffs.length === 0) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
        Configuration Differences
      </h4>
      <div className="space-y-3">
        {significantDiffs.map(diff => (
          <div key={diff.key} className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
              {formatMetricName(diff.key)}
            </p>
            <div className="grid grid-cols-3 gap-2">
              {diff.values.map((v, i) => (
                <div key={i} className="text-sm">
                  <span className="text-text-tertiary dark:text-text-secondary">{v.scenario}: </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatConfigValue(v.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatMetricName(metric: string): string {
  return metric
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
}

function formatValue(value: number, metric: string): string {
  if (metric.includes('rate') || metric.includes('share') || metric.includes('percent')) {
    return `${(value * 100).toFixed(1)}%`;
  }
  if (metric.includes('price') || metric.includes('revenue') || metric.includes('value')) {
    return `$${value.toLocaleString()}`;
  }
  return value.toLocaleString();
}

function formatConfigValue(value: unknown): string {
  if (value === undefined || value === null) return '-';
  if (typeof value === 'number') {
    if (value < 1 && value > 0) return `${(value * 100).toFixed(0)}%`;
    return value.toLocaleString();
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}
